import { KEY_CODES } from "../../../gearshiftGame/constants";
import { getLegalActions } from "./aiAgentLegalActions";
import {
  FRONTBUS_DESCRIPTIONS,
  HQ_PORTRAIT_DESCRIPTIONS,
  MISSION0_CUE_BADGES,
  MISSION0_PACKAGE_DESCRIPTIONS,
  MISSION3_PACKAGE_DESCRIPTIONS,
  MISSION3_TARGET_CAR_DESCRIPTIONS,
  describeCue,
  describeLicensePlate,
} from "./aiAgentPublicCues";

const warnedFiles = new Set();

function warnOnce(message, file) {
  const key = `${message}:${file}`;
  if (warnedFiles.has(key)) return;
  warnedFiles.add(key);
  console.warn(`[aiAgent] ${message}`, file);
}

/**
 * The lane labels the footer actually renders ("dark", "odd", ...), as `{ label: lane }` -
 * read off the page because some footer variants show keycaps without labels.
 */
function readVisibleLaneMapping() {
  const mapping = {};
  document.querySelectorAll(".lane-labels-lane-key").forEach((key) => {
    const label = key.querySelector(".lane-labels-lane-key-label")?.textContent?.trim().toLowerCase();
    const lane = KEY_CODES[key.dataset.keyCode];
    if (label && lane != null) mapping[label] = lane;
  });
  return mapping;
}

/** The HQ message text the panel actually renders (keys already resolved for the layout), or null. */
function readHQMessageText() {
  const text = document.querySelector('#hq-message-panel [data-hq-main-text="true"]')?.textContent;
  const normalized = text?.replace(/\s+/g, " ").trim();
  return normalized || null;
}

/** The rendered file name of the HQ portrait image shown beside the message, or null. */
function readHQPortraitFile() {
  const src = document.querySelector("#hq-message-panel .gear-hq-portrait-img")?.getAttribute("src");
  return src ? src.split("/").pop() : null;
}

// HQ messages (phase 6) are a choice of their own: the agent reads the message and continues.
// Surveys also use phase 6 but render no message panel, so they stay withheld.
function hqMessageObservation(missionNumber, trial, legalActions) {
  const hqMessage = readHQMessageText();
  if (!hqMessage) return null;
  const hqPortrait = describeCue(HQ_PORTRAIT_DESCRIPTIONS, readHQPortraitFile());
  if (!hqPortrait) {
    warnOnce("no description for HQ portrait - agent choice withheld", readHQPortraitFile());
    return null;
  }
  return {
    mission: `mission${missionNumber}`,
    trial,
    phase: "hq_message",
    available_actions: legalActions.map((action) => action.agentAction),
    task_observation: { hq_message: hqMessage, hq_portrait: hqPortrait },
  };
}

// The plate is on the sign from the start of phase 3, so every lane choice carries it.
function licensePlateObservation(trialConfig) {
  const licensePlate = describeLicensePlate(trialConfig.stimulusFile);
  if (!licensePlate) warnOnce("license plate file doesn't parse - agent choice withheld", trialConfig.stimulusFile);
  return licensePlate;
}

function mission0Observation(game, trial, legalActions) {
  const phase = game.gamePhase === 1 ? "attend" : "respond";
  const observation = {
    mission: "mission0",
    trial,
    phase,
    available_actions: legalActions.map((action) => action.agentAction),
  };
  // Attend is only offered once the target is in lock-on range (see getLegalActions), and the
  // package isn't on screen until phase 2 - so it carries nothing else.
  if (phase === "attend") return observation;

  const trialConfig = game.trialConfig || {};
  const cueBadge = describeCue(MISSION0_CUE_BADGES, trialConfig.cueFile);
  if (!cueBadge) {
    warnOnce("no description for Mission 0 cue badge - agent choice withheld", trialConfig.cueFile);
    return null;
  }
  const visiblePackageId = describeCue(MISSION0_PACKAGE_DESCRIPTIONS, trialConfig.packageFile);
  if (!visiblePackageId) {
    warnOnce("no visual description for Mission 0 package - agent choice withheld", trialConfig.packageFile);
    return null;
  }
  const licensePlate = licensePlateObservation(trialConfig);
  if (!licensePlate) return null;
  const taskObservation = { cue_badge: { ...cueBadge }, license_plate: licensePlate };
  const visibleLaneMapping = readVisibleLaneMapping();
  if (Object.keys(visibleLaneMapping).length) taskObservation.visible_lane_mapping = visibleLaneMapping;

  observation.task_observation = taskObservation;
  observation.package_observation = { visible_package_id: { ...visiblePackageId } };
  return observation;
}

function mission3Observation(game, trial, legalActions) {
  // Attend is only offered once the target is in lock-on range (see getLegalActions), same as
  // Mission 0's approach - no package/lane evidence exists yet, so it carries nothing else. This
  // is a new phase for Mission 3's wire shape; the existing lane-choice shape below is untouched.
  if (game.gamePhase === 1) {
    return {
      mission: "mission3",
      trial,
      phase: "attend",
      available_actions: legalActions.map((action) => action.agentAction),
    };
  }

  const trialConfig = game.trialConfig || {};
  const canEscape = legalActions.some((action) => action.agentAction === "escape");
  const canPersist = legalActions.some((action) => action.agentAction.startsWith("persist:"));

  // The target car (trialConfig.cueFile) is Mission 3's only rule cue, and an implicit one - no
  // on-screen text says what it means, only repeated exposure teaches an association. It's carried
  // here rather than in the attend observation above, matching how Mission 0's cue_badge is sent
  // in respond even though it was already visible during attend.
  const targetCar = describeCue(MISSION3_TARGET_CAR_DESCRIPTIONS, trialConfig.cueFile);
  if (!targetCar) {
    warnOnce("no description for Mission 3 target car - agent choice withheld", trialConfig.cueFile);
    return null;
  }
  const persistencePackage = describeCue(MISSION3_PACKAGE_DESCRIPTIONS, trialConfig.packageFile);
  if (!persistencePackage) {
    warnOnce("no description for Mission 3 package - agent choice withheld", trialConfig.packageFile);
    return null;
  }
  const licensePlate = licensePlateObservation(trialConfig);
  if (!licensePlate) return null;
  const optionObservation = {
    target_car: { ...targetCar },
    persistence_package: { ...persistencePackage },
  };
  if (canEscape) {
    const escapePackage = describeCue(MISSION3_PACKAGE_DESCRIPTIONS, trialConfig.escapePackage);
    if (!escapePackage) {
      warnOnce("no description for Mission 3 escape package - agent choice withheld", trialConfig.escapePackage);
      return null;
    }
    optionObservation.escape_package = { ...escapePackage };
  }

  const observation = {
    mission: "mission3",
    trial,
    available_actions: [...(canPersist ? ["persist"] : []), ...(canEscape ? ["escape"] : [])],
    task_observation: { license_plate: licensePlate },
    option_observation: optionObservation,
  };
  if (game.frontbusOnTime != null) {
    const frontbusCue = describeCue(FRONTBUS_DESCRIPTIONS, trialConfig.frontbusImage);
    if (frontbusCue) observation.escape_risk_observation = { frontbus_ordinal_cue: { ...frontbusCue } };
  }
  return observation;
}

/**
 * Research-agent fields for one snapshot: `legalActions`, plus the public `agentObservation`
 * when the agent has a choice to make. When the observation can't be built, `legalActions` is
 * emptied too, so the agent server skips the state instead of deciding on partial evidence.
 * @returns {{ legalActions: object[], agentObservation: object | null }}
 */
export function buildAgentDecision(game, missionNumber, trialIndex) {
  const legalActions = getLegalActions(game, missionNumber);
  if (!legalActions.length || !Number.isInteger(trialIndex)) return { legalActions: [], agentObservation: null };

  let agentObservation = null;
  if (game.gamePhase === 6) agentObservation = hqMessageObservation(missionNumber, trialIndex, legalActions);
  else if (missionNumber === 0) agentObservation = mission0Observation(game, trialIndex, legalActions);
  else if (missionNumber === 3) agentObservation = mission3Observation(game, trialIndex, legalActions);
  return agentObservation ? { legalActions, agentObservation } : { legalActions: [], agentObservation: null };
}

/**
 * Hands out `actionOpportunityId`s: the same id for every snapshot of one choice, a new one the
 * moment the choice changes - a new game, phase, public evidence, or set of legal actions. The
 * agent server reuses its decision for a repeated id and rejects an id whose evidence changed,
 * so the id has to follow the evidence exactly rather than trialIndex + phase.
 */
export class OpportunityTracker {
  constructor() {
    this.gameIds = new WeakMap();
    this.nextGameId = 1;
    this.lastKey = null;
    this.currentId = null;
    this.currentOnsetMs = null;
    this.counter = 0;
  }

  /** @returns {string | null} null when there is no choice open */
  idFor(game, agentObservation, legalActions) {
    if (!game || !agentObservation) {
      this.lastKey = null;
      this.currentId = null;
      this.currentOnsetMs = null;
      return null;
    }
    if (!this.gameIds.has(game)) this.gameIds.set(game, this.nextGameId++);
    const gameId = this.gameIds.get(game);
    const key = JSON.stringify([gameId, game.gamePhase, agentObservation, legalActions]);
    if (key !== this.lastKey) {
      this.lastKey = key;
      this.counter += 1;
      this.currentId = `game${gameId}-phase${game.gamePhase}-choice${this.counter}`;
      // First frame the choice was open - GF04's start for choice-onset-to-action timing.
      this.currentOnsetMs = performance.now();
    }
    return this.currentId;
  }

  /** @returns {number | null} performance.now() at which `opportunityId` opened, while it's still the open one */
  onsetFor(opportunityId) {
    return opportunityId && opportunityId === this.currentId ? this.currentOnsetMs : null;
  }
}
